import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { createPatchObj } from '@racemap/utilities/functions/createPatchObj';
import { isDefined } from '@racemap/utilities/functions/validation';
import {
  activateImpersonation,
  deactivateImpersonation,
  getImpersonatedUserId,
} from '@racemap/utilities/impersonateUserMode';
import type { User_Legacy } from '@racemap/utilities/types/users';
import type { PathInto, TypeOfPath } from '@racemap/utilities/types/utils';
import { type Immutable, produce } from 'immer';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import type { DraftState, State } from '../reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

// Only /users/current marks an impersonation. Any other read of the impersonated account would
// drop the mark, and with it the way back to the admin, so it is carried over.
const putUser = (s: DraftState, userId: string, user: User_Legacy) => {
  const isImpersonated =
    userId === s.users.currentUserId && s.users.items.get(userId)?.impersonated === true;
  s.users.items.set(userId, isImpersonated ? { ...user, admin: false, impersonated: true } : user);
};

export interface UserState {
  users: {
    items: Map<string, User_Legacy>;
    currentUserId: null | string;
    isSaving: boolean;
    isLoadingCurrentUser: boolean;
    getter: {
      currentUser: () => Immutable<User_Legacy> | null;
      user: (userId: string | null | undefined) => Immutable<User_Legacy> | null;
      isAdmin: () => boolean;
    };
    actions: {
      loadUser: (userId: string) => Promise<User_Legacy>;
      loadUsers: () => Promise<void>;
      loadCurrentUser: () => Promise<void>;
      removeUser: (userId: string) => Promise<void>;
      updateUser: <P extends PathInto<User_Legacy>>(
        userId: string,
        userUpdates: Array<{ key: P; newValue: TypeOfPath<User_Legacy, P> }>,
      ) => Promise<void>;
      impersonateUser: (userId: string) => Promise<void>;
      stopImpersonation: () => Promise<void>;
      login: (email: string, password: string) => Promise<void>;
      logout: () => Promise<void>;
      register: (args: {
        name: string;
        email: string;
        password: string;
        company?: string;
        phone?: string;
        country: string;
        trackingConsent?: boolean;
      }) => Promise<void>;
      resetPassword: (password: string, userId: string, token: string) => Promise<void>;
      requestPasswordReset: (email: string) => Promise<void>;
      confirmEmail: (email: string, code: string) => Promise<void>;
      syncMailchimpTags: (
        userId: string,
        tagsToAdd: Array<string>,
        tagsToRemove?: Array<string>,
      ) => Promise<{ mailchimpTags: Array<string> }>;
    };
  };
}

export const createUserStore = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): UserState => ({
  users: {
    items: new Map(),
    currentUserId: null,
    isSaving: false,
    isLoadingCurrentUser: true, // after side loading the first action is to start the loading of the current user
    getter: {
      currentUser: () => {
        const currentUserId = get().users.currentUserId || '';
        return get().users.items.get(currentUserId) || null;
      },
      isAdmin: () => {
        const currentUserId = get().users.currentUserId || '';
        return get().users.items.get(currentUserId)?.admin || false;
      },
      user: (userId) => {
        if (userId == null) return null;
        return get().users.items.get(userId) || null;
      },
    },
    actions: {
      loadUser: async (userId) => {
        const user = await apiClient.getUser(userId);
        set(
          produce((s: DraftState) => {
            putUser(s, userId, user);
          }),
        );

        return user;
      },
      loadUsers: async () => {
        const { data: users } = await apiClient.getUsers();
        set(
          produce((s: DraftState) => {
            users.forEach((user) => {
              putUser(s, user.id, user);
            });
          }),
        );
      },
      loadCurrentUser: async () => {
        set(
          produce((s) => {
            s.users.isLoadingCurrentUser = true;
          }),
        );
        try {
          const user = await apiClient.getCurrentUser();
          // The server marks an applied impersonation. A locally stored target the
          // server did not honour (deleted user, expired session, admin flag
          // revoked) would otherwise leave the UI claiming an impersonation that
          // is not in effect.
          if (getImpersonatedUserId() != null && user.impersonated !== true) {
            deactivateImpersonation();
          }
          set(
            produce((s) => {
              s.users.items.set(user.id, user);
              s.users.currentUserId = user.id;
              s.users.isLoadingCurrentUser = false;
            }),
          );
        } catch {
          set(
            produce((s) => {
              s.users.isLoadingCurrentUser = false;
            }),
          );
        }
      },
      removeUser: async (userId) => {
        await apiClient.removeUser(userId);
        set(
          produce((s) => {
            s.users.items.delete(userId);
          }),
        );
      },
      updateUser: async (userId, updates) => {
        set(
          produce((s: DraftState) => {
            s.users.isSaving = true;
          }),
        );
        let currentUserState = get().users.items.get(userId);

        try {
          if (currentUserState == null) {
            currentUserState = await get().users.actions.loadUser(userId);
          }

          if (!isDefined(currentUserState)) {
            throw new Error(`Fail User Update. User with userId ${userId} is unknown.`);
          }

          // create the patch object, that only consist with the updates
          const patchObj = createPatchObj<User_Legacy>(updates, currentUserState as User_Legacy);
          set(
            produce((s: DraftState) => {
              s.users.items.set(
                userId,
                Object.assign(s.users.items.get(userId) || {}, patchObj as User_Legacy),
              );
            }),
          );

          const updatedUser = await apiClient.updateUser(userId, patchObj);
          set(
            produce((s: DraftState) => {
              // The update answer types its id as an ObjectId; the store has always held it as is.
              putUser(s, userId, updatedUser as unknown as User_Legacy);
            }),
          );
        } catch (error) {
          if (error instanceof HTTPFetchError && error.serverError != null) {
            toast.error(error.serverError);
          }

          set(
            produce((s: DraftState) => {
              if (isDefined(currentUserState)) {
                s.users.items.set(userId, Object.assign(currentUserState));
              }
            }),
          );
        }
        set(
          produce((s: DraftState) => {
            s.users.isSaving = false;
          }),
        );
      },
      // Both persist the target so the api-client sends the header on every
      // request and the mode survives a reload, then reload: the store-based and
      // SWR-based lists have no shared revalidation path, and every one of them
      // now answers for a different user.
      impersonateUser: async (userId) => {
        activateImpersonation(userId);
        window.location.reload();
      },
      stopImpersonation: async () => {
        deactivateImpersonation();
        window.location.reload();
      },
      login: async (email, password) => {
        // A fresh login is a new identity — never inherit a stale target.
        deactivateImpersonation();
        await apiClient.login(email, password);
        const user = await apiClient.getCurrentUser();

        set(
          produce((s) => {
            s.users.items.set(user.id, user);
            s.users.currentUserId = user.id;
          }),
        );
      },
      logout: async () => {
        // Clear the target so it cannot leak into the next session. Clearing
        // before the call also keeps the header off this request, which the
        // read-only guard would otherwise reject.
        deactivateImpersonation();
        await apiClient.logout();
        set(
          produce((s) => {
            s.users.currentUserId = null;
          }),
        );
      },
      register: async (data) => {
        await apiClient.register(data);
      },
      resetPassword: async (password, userId, token) => {
        await apiClient.resetPassword(password, userId, token);
      },
      requestPasswordReset: async (email) => {
        await apiClient.requestPasswordReset(email);
      },
      confirmEmail: async (email, code) => {
        // confirming logs in, so it is a fresh identity like login
        deactivateImpersonation();
        // the response is the full user, so a failed follow-up read can't turn success into an error
        const user = await apiClient.confirmEmail(email, code);

        set(
          produce((s) => {
            s.users.items.set(user.id, user);
            s.users.currentUserId = user.id;
          }),
        );
      },
      syncMailchimpTags: async (userId, tagsToAdd, tagsToRemove = []) => {
        const result = await apiClient.syncMailchimpTags(userId, tagsToAdd, tagsToRemove);
        // Reload user to get the fresh mailchimp tags from the view endpoint
        await get().users.actions.loadUser(userId);
        return result;
      },
    },
  },
});
